import {
  FormProvider, SubmitHandler, useForm,
} from 'react-hook-form';

import {
  IncomeDataT,
  incomeSourceDisclaimer,
  IncomeSourceFormT,
  incomeSourceOptions,
  IncomeSourceValueT,
  isBenefitsIncomeSource,
  isEmployed,
} from '@consumer-frontend/nc-utils';
import { Fieldset } from '@consumer-frontend/shared-ui';

import { NcIncomeSourceBenefitsForm } from './NcIncomeSourceBenefitsForm';
import { NcIncomeSourceEmployedForm } from './NcIncomeSourceEmployedForm';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcSelect } from '../NcSelect';

export type NcIncomeSourceFormPropsT = {
  incomeData: IncomeDataT;
  onSubmitComplete: (data: IncomeDataT) => Promise<void>;
  onBack?: () => void;
  resetToInitialValues?: boolean;
  showAmountField?: boolean;
};

export type NcIncomeSourceFormT = React.FC<NcIncomeSourceFormPropsT>;

export const NcIncomeSourceForm: NcIncomeSourceFormT = ({
  incomeData,
  onBack,
  onSubmitComplete,
  resetToInitialValues = false,
  showAmountField = false,
}) => {
  const methods = useForm<IncomeSourceFormT>({
    defaultValues: {
      amount: incomeData.amount,
      benefits: incomeData.benefits,
      employment: incomeData.employment,
      source: incomeData.source,
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    reset,
    unregister,
  } = methods;

  const onSubmit: SubmitHandler<IncomeSourceFormT> = async (data) => {
    await onSubmitComplete(data);
  };

  const incomeSourceValidations = {
    required: 'Please select an income source.',
    validate: (option?: IncomeSourceValueT) => {
      return incomeSourceOptions.some((opt) => opt.value === option)
        || 'Please select a valid income source.';
    },
  };

  const onSourceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    unregister('benefits');
    unregister('employment');

    if (showAmountField) {
      unregister('amount');
    }

    if (resetToInitialValues) {
      if (incomeData.source === event.target.value) {
        reset({
          amount: incomeData.amount,
          benefits: incomeData.benefits,
          employment: incomeData.employment,
          source: incomeData.source,
        });
      }
    }
  };

  const selectedIncomeSource = watch('source');
  const showEmployedForm = isEmployed(selectedIncomeSource);
  const showBenefitsForm = isBenefitsIncomeSource(selectedIncomeSource);

  return (
    <FormProvider {...methods}>
      <NcForm onSubmit={handleSubmit(onSubmit)}>
        <NcSelect
          {...register('source', { ...incomeSourceValidations, onChange: onSourceChange })}
          errorMessage={errors.source && 'Select your primary source of income.'}
          id="income-source"
          label="Primary Income Source"
          options={incomeSourceOptions}
          placeholder="Select income source"
        />

        {showEmployedForm && (
          <NcIncomeSourceEmployedForm
            key={selectedIncomeSource}
            showAmountField={showAmountField}
          />
        )}

        {showBenefitsForm && (
          <NcIncomeSourceBenefitsForm
            key={selectedIncomeSource}
            showAmountField={showAmountField}
          />
        )}

        <p className="disclaimer above-button">
          {incomeSourceDisclaimer}
        </p>

        <Fieldset className="cta-buttons">
          <NcButton type="submit">Continue</NcButton>

          {onBack && (
            <NcButton onClick={onBack} variant="outline-primary">Back</NcButton>
          )}
        </Fieldset>
      </NcForm>
    </FormProvider>
  );
};
